import { useContext, useEffect, useReducer, useMemo } from "react"
import { GetServerSideProps } from "next"
import Router from "next/router"
import { Container, Grid, Box } from "@mui/material/"
import { useSnackbar } from "notistack"

import SEO from "components/SEO"
import BreadCrumbs, { BreadCrumbAncestor, getCountryNameFromLocale } from "components/BreadCrumbs"
import SliderV3 from "containers/ProductDetailPage/ProductHeader/Sliderv3"
import RatingPrintCompare from "containers/ProductDetailPage/ProductHeader/RatingPrintCompare"
import PriceAndDelivery from "containers/ProductDetailPage/ProductHeader/PriceAndDelivery"
import ConfigurationsHandler from "containers/ProductDetailPage/ProductHeader/ConfigurationsHandler"
import ColorSelect from "containers/ProductDetailPage/ProductHeader/Configurations/subComponents/ColorSelect"
import ScrollToConfigurationsButton from "containers/ProductDetailPage/ProductHeader/ScrollToConfigurationsButton"
import Attributes from "containers/ProductDetailPage/ProductHeader/Attributes"
import StickyProductInfo from "containers/ProductDetailPage/ProductContent/components/StickyProductInfo"
import ProductDetailPageAccordion from "containers/ProductDetailPage/ProductContent/ProductDetailPageAccordion"
import ProductStructuredData from "components/StructuredData/ProductStructuredData"

import useProductReviews from "@secureo/common/hooks/data/useProductReviews"
import useWindowSize from "hooks/useWindowSize"
import useTranslation from "hooks/useTranslation"
import useDeliveryOptions from "@secureo/common/hooks/data/useDeliveryOptions"

import AppContext from "@secureo/common/context/AppContext/AppContext"
import reducer, {
	setQuantity,
	addProductAndConfigurationsToCart,
	resetReducerState,
	DispatchProductDetailPageReducer,
	ProductDetailPageReducerState,
} from "@secureo/common/reducers/productDetailPageReducer"
import getInitialReducerState from "@secureo/common/reducers/productDetailPageReducer/utils/getInitialReducerState"

import { makeStyles } from "makeStyles"
import { anonymousAccessTokenCache } from "@secureo/common/utils/commerceTools/token/accessTokenCache"
import fetchProduct from "@secureo/common/utils/commerceTools/graphql/queries/fetchProduct"
import {
	trackGA4ViewItem,
	trackProductDetailImpression,
} from "@secureo/common/utils/tracking/productDetail"
import { isGoogleBotUserAgent } from "@secureo/common/utils/detection/userAgent"
import fetchProductReviewsByProductId from "@secureo/common/utils/commerceTools/graphql/queries/fetchProductReviewsByProductId"
import normalizeLocale from "@secureo/common/utils/normalizeLocale"
import serializeJSON from "@secureo/common/utils/serializeJSON"
import isBrowser from "@secureo/common/utils/isBrowser"
import { isSwitzerlandLocale } from "utils/seo/pages/fallback"
import { flatFeesByCurrencyCode } from "@secureo/common/config/shippingZonesAndCosts"
import { getServerTranslations } from "utils/i18n/getServerTranslations"

import { CountryCode } from "@secureo/common/typings/CountryCode"
import { Product, Review } from "@secureo/common/typings/Product"
import { CurrencyCode } from "@secureo/common/typings/Forex"
import getCurrencySymbol from "@secureo/common/utils/forex/getCurrencySymbol"

const useStyles = makeStyles()((theme) => ({
	root: {
		flexGrow: 1,
	},
	price: {
		padding: 0,
		margin: 0,
		color: theme.palette.secondary.main,
	},
	h1: {
		padding: "0",
		margin: "0",
		fontSize: "1.4rem",
	},
}))

const getMetaTitle = (name: string, locale: string, language: string) => {
	switch (language) {
		case "pl":
		case "de":
		default:
			return `${name} | SafeHero ${getCountryNameFromLocale(locale)}`
	}
}

const getMetaDescription = (
	name: string,
	locale: string,
	language: string,
	currencyCode: CurrencyCode,
) => {
	switch (language) {
		case "pl":
			return `${name} - zamów teraz bezpośrednio online na SafeHero ${getCountryNameFromLocale(
				locale,
			)}! ✓ Wysyłka gratis od ${
				flatFeesByCurrencyCode[currencyCode].flatFeeForSmallAmountThresholdGrossCents / 100
			}${getCurrencySymbol(currencyCode)} ✓ Trusted Shop®${
				isSwitzerlandLocale(locale) ? "" : " ✓ Zakup na rachunek"
			}`
		case "de":
		default:
			return `${name} - jetzt direkt online bestellen auf SafeHero ${getCountryNameFromLocale(
				locale,
			)}! ✓ Portofrei ab ${
				flatFeesByCurrencyCode[currencyCode].flatFeeForSmallAmountThresholdGrossCents / 100
			}${getCurrencySymbol(currencyCode)} ✓ Trusted Shop®${
				isSwitzerlandLocale(locale) ? "" : " ✓ Kauf auf Rechnung"
			}`
	}
}

const ProductPage = ({
	product,
	reviews: ssrReviews,
	slug,
	language,
	countryCode,
	currencyCode,
	locale,
	isDebug,
	initialVariantSku: initialSSRVariantSku,
}: Props) => {
	const { classes } = useStyles()
	const { t } = useTranslation("product")
	const { enqueueSnackbar } = useSnackbar()
	const appContextState = useContext(AppContext)
	const { innerWidth } = useWindowSize()
	const isMobile = innerWidth < 576

	const { ancestorBreadCrumbs = [], reviewRatingStatistics } = product ?? {}
	const mostSpecificBreadCrumbCategoryName = [...ancestorBreadCrumbs].reverse()?.[0]?.label ?? ""

	const initialVariantSku = isBrowser()
		? new URLSearchParams(window.location.search).get("variant")
		: initialSSRVariantSku

	const [reducerState, dispatch] = useReducer(
		reducer,
		getInitialReducerState(
			product,
			countryCode,
			locale,
			"SH",
			isDebug,
			initialVariantSku,
			currencyCode,
		),
	) as [ProductDetailPageReducerState, DispatchProductDetailPageReducer]

	const {
		variantsByVariantId = {},
		name,
		shortDescription,
		images,
		attributes = {},

		// Price
		singleGrossPriceCents,

		// Safe
		colorsBySafeLockConfigurationProductSku,
	} = reducerState

	const configurationGroups = attributes?.configurationGroups ?? []

	const accessToken = appContextState?.accessToken ?? null
	const masterVariantSku = variantsByVariantId?.[1]?.sku

	useEffect(() => {
		// Prevent 404 tracking
		if (product?.id) {
			resetReducerState(
				dispatch,
				product,
				countryCode,
				"SH",
				locale,
				isDebug,
				initialVariantSku,
				currencyCode,
			)

			trackProductDetailImpression(
				name,
				masterVariantSku,
				singleGrossPriceCents,
				attributes.manufacturer,
				mostSpecificBreadCrumbCategoryName,
				mostSpecificBreadCrumbCategoryName,
			)

			trackGA4ViewItem(
				name,
				masterVariantSku,
				singleGrossPriceCents,
				attributes.manufacturer,
				mostSpecificBreadCrumbCategoryName,
			)
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [product?.id, countryCode])

	const deliveryOptionSkus = useMemo(
		() =>
			attributes?.deliveryOptionsByDestinationCountryCode?.[countryCode]?.map(
				(deliveryOption) => deliveryOption.sku,
			),
		[attributes?.deliveryOptionsByDestinationCountryCode, countryCode],
	)
	const { deliveryOptionsBySku } = useDeliveryOptions(accessToken, deliveryOptionSkus, language)
	const { reviews } = useProductReviews(accessToken, product?.id, ssrReviews)

	const hasMultipleColors =
		colorsBySafeLockConfigurationProductSku?.[attributes?.variantConfigurationProductSku]
			?.length > 1

	const previewImage = images?.[0]

	const addToCart = async () => {
		const hasSuccessFullyAddedProductToCart = await addProductAndConfigurationsToCart(
			dispatch,
			appContextState,
			reducerState,
			language,
			countryCode,
			mostSpecificBreadCrumbCategoryName,
			currencyCode,
		)
		if (hasSuccessFullyAddedProductToCart) {
			enqueueSnackbar(t("addedToCart"), {
				variant: "success",
			})

			Router.push(`/cart`)
		} else
			enqueueSnackbar(t("somethingWentWrong"), {
				variant: "error",
			})
	}

	const updateQuantity = (quantity: number) => {
		setQuantity(dispatch, reducerState, quantity)
	}

	const asPath = `/p/${slug}`
	const breadcrumb: BreadCrumbAncestor = {
		label: name,
		href: asPath,
	}

	return (
		<>
			<SEO
				asPath={asPath}
				pageType={"productDetail"}
				title={getMetaTitle(name, locale, language)}
				description={getMetaDescription(name, locale, language, currencyCode)}
				openGraph={{
					images: [{ url: previewImage, width: 800, height: 800 }],
				}}
			/>
			<div className={classes.root}>
				<BreadCrumbs breadCrumbsPaths={ancestorBreadCrumbs} currentPath={breadcrumb} />
				<div style={{ background: "white" }}>
					<Box mb={5} py={5}>
						<Container>
							{isMobile && (
								<>
									<div style={{ width: "100%" }}>
										<h1 className={classes.h1}>{name}</h1>
									</div>
									<Box mb={2}>
										<RatingPrintCompare
											averageRating={reviewRatingStatistics?.averageRating}
										/>
									</Box>
								</>
							)}
							<Grid container spacing={3}>
								<Grid item xs={12} sm={6} md={5} style={{ position: "relative" }}>
									<SliderV3 images={images} />
								</Grid>
								{!isMobile ? (
									<Grid item xs={12} sm={6} md={7}>
										<Grid container spacing={2}>
											<Grid item xs={12}>
												<Box mt={2}>
													<h1 className={classes.h1}>{name}</h1>
												</Box>
												<RatingPrintCompare
													averageRating={
														reviewRatingStatistics?.averageRating
													}
												/>
												<PriceAndDelivery
													addToCart={addToCart}
													reducerState={reducerState}
													setQuantity={updateQuantity}
													deliveryOptionsBySku={deliveryOptionsBySku}
													language={language}
												/>
												<ConfigurationsHandler
													accessToken={accessToken}
													reducerState={reducerState}
													dispatch={dispatch}
												/>
												{hasMultipleColors && (
													<ColorSelect
														reducerState={reducerState}
														dispatch={dispatch}
														language={language}
													/>
												)}
												{configurationGroups?.length > 0 && (
													<ScrollToConfigurationsButton />
												)}
												<Attributes
													attributes={attributes}
													shortDescription={shortDescription}
													language={language}
												/>
											</Grid>
										</Grid>
									</Grid>
								) : (
									<Grid item xs={12}>
										<PriceAndDelivery
											addToCart={addToCart}
											reducerState={reducerState}
											setQuantity={updateQuantity}
											deliveryOptionsBySku={deliveryOptionsBySku}
											language={language}
										/>
										<ConfigurationsHandler
											accessToken={accessToken}
											reducerState={reducerState}
											dispatch={dispatch}
										/>
										{hasMultipleColors && (
											<ColorSelect
												reducerState={reducerState}
												dispatch={dispatch}
												language={language}
											/>
										)}
										{configurationGroups?.length > 0 && (
											<ScrollToConfigurationsButton />
										)}
										<Attributes
											attributes={attributes}
											shortDescription={shortDescription}
											language={language}
										/>
									</Grid>
								)}
							</Grid>
							<StickyProductInfo
								reducerState={reducerState}
								addToCart={addToCart}
								setQuantity={updateQuantity}
							/>
						</Container>
					</Box>
				</div>
				<Box mb={5}>
					<Container>
						<ProductDetailPageAccordion
							accessToken={accessToken}
							reducerState={reducerState}
							dispatch={dispatch}
							averageRating={reviewRatingStatistics?.averageRating}
							reviews={reviews}
							language={language}
						/>
					</Container>
				</Box>
				<ProductStructuredData
					product={product}
					reviews={reviews}
					locale={locale}
					singleGrossPriceCents={singleGrossPriceCents}
				/>
			</div>
		</>
	)
}

interface Props {
	product: Product
	reviews: Review[]
	slug: string
	locale: string
	language: string
	countryCode: CountryCode
	currencyCode: CurrencyCode
	isDebug: boolean
	initialVariantSku: string
}

export const getServerSideProps: GetServerSideProps = async (context) => {
	const { req, query, res, locale: rawLocale } = context

	const accessToken = await anonymousAccessTokenCache()
	const slug = context.params?.slug as string

	const isDebug = query.debug !== undefined
	const initialVariantSku = query.variant as string

	const { locale, language, countryCode } = normalizeLocale(rawLocale)
	const currencyCode: CurrencyCode = countryCode === "PL" ? "PLN" : "EUR"
	const product = await fetchProduct(
		accessToken,
		slug,
		locale,
		"SH",
		countryCode,
		language,
		currencyCode,
	)

	if (!product) {
		res.statusCode = 404
		return {
			notFound: true,
			props: {
				error: "Product not found",
			},
		}
	}

	let reviews: Review[] = []

	// Reviews are only fetched on SSR, if request user agent matches Google Bot (So it can be included in structured data)
	if (product && isGoogleBotUserAgent(req)) {
		reviews = await fetchProductReviewsByProductId(accessToken, product?.id)
	}

	const props: Props = {
		product,
		reviews,
		slug,
		locale,
		language,
		countryCode,
		currencyCode,
		isDebug,
		initialVariantSku,
		...(await getServerTranslations(locale, ["common", "countries", "languages", "product"])),
	}

	res.setHeader("Cache-Control", "s-maxage=5, stale-while-revalidate=5")

	return {
		props: serializeJSON(props),
	}
}

export default ProductPage
